import { Grid, useEventCallback } from '@mui/material';
import { memo } from 'react';
import FormGenerality from './accordion-gen';
import { AccordionGeneralityForm } from 'shell/view-models/accordion-generality-form';
import Table from 'shell/components/shared/table';
import { noop } from 'shell/utils/noop';
import { FormikErrors, FormikProps, FormikTouched } from 'formik';
import { __PERSON_API_URL__ } from 'shell/constants/environment';
import { transformerPersonToRegisterOffice } from 'shell/components/shared/sipo-person-form/transformer-person-to-register-office';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { HttpResponse } from 'shell/models/http-response';
import { ResponseAnprData } from 'shell/models/person';
import AccordionFilter from 'shell/components/shared/search-person/accordion-filter';
import { BodyTablePersonsVM, PersonalDataVM } from 'shell/view-models/personal-data';
import { headCells } from 'shell/components/shared/search-person';
import AccordionFilterAccountHolder from 'practice/components/edit/form/step-register-office/accordions/accountholder-generality/accordion-filter-practice-accountholder';
import { RegisterOfficeVM } from 'shell/view-models/register-office-practice-vm';

interface GeneralityComponentProps {
  onChangeFields: (values: PersonalDataVM) => void;
  onChange: (key: keyof PersonalDataVM, value: PersonalDataVM[keyof PersonalDataVM]) => void;
  values?: PersonalDataVM;
  onSearch: () => void;
  errors?: FormikErrors<PersonalDataVM>;
  touched?: FormikTouched<PersonalDataVM>;
  disabled?: boolean;
  url?: string;
  isApplicant?: boolean;
  disabledGenerality?: boolean;
  typeRegister?: string;
  yearId?: string;
  progressive?: string;
  setOpenModalNewPerson?: (value: boolean, idSipo?: string, idAnpr?: string) => void;
  isSearchPersonFilterForAccountHolder?: boolean;
  formContext?: FormikProps<RegisterOfficeVM>;
  personFilterAccountholderBlur?: (fieldName: string) => void;
  onChoicePerson?: (values: RegisterOfficeVM) => void;
}

const GeneralityComponent = ({
  onChangeFields,
  onChange,
  values,
  onSearch,
  errors,
  touched,
  disabled,
  isApplicant,
  disabledGenerality,
  typeRegister,
  yearId,
  progressive,
  setOpenModalNewPerson,
  url = `${__PERSON_API_URL__}/persona/pratiche/ricerca-intestatario`,
  isSearchPersonFilterForAccountHolder = false,
  personFilterAccountholderBlur = () => {
    return;
  },
  onChoicePerson,
}: GeneralityComponentProps): React.ReactElement => {
  // ricerca sia su ANPR che DB
  const getPerson = useHttpClient<HttpResponse<ResponseAnprData>, RegisterOfficeVM>(url, {
    options: {
      errorCatching: true,
      method: 'POST',
      loading: true,
      data: {},
    },
    transformer: (response) => transformerPersonToRegisterOffice(response, isApplicant),
  });

  const onFieldGeneralityChange = useEventCallback(
    (
      key: keyof AccordionGeneralityForm,
      value: AccordionGeneralityForm[keyof AccordionGeneralityForm]
    ) => {
      onChange('generality', {
        ...(values?.generality || {}),
        [key]: value,
      });
    }
  );

  const onFieldsGeneralityChange = useEventCallback((valuesGenerality: AccordionGeneralityForm) => {
    onChange('generality', {
      ...(values?.generality || {}),
      ...valuesGenerality,
    });
  });

  const onChangePage = useEventCallback((page: number) => {
    onChange('table', {
      ...(values?.table || {}),
      page: page || 0,
    });
  });

  const onChangeRowsPerPage = useEventCallback((rowsPerPage: number) => {
    onChange('table', {
      ...(values?.table || {}),
      page: 0,
      rowsPerPage: rowsPerPage || 0,
    });
  });

  const onVisibilityPress = useEventCallback(
    (cell: BodyTablePersonsVM): Promise<BodyTablePersonsVM> => {
      return new Promise(() => {
        (
          getPerson({
            data: {
              tipoRegistro: typeRegister,
              idAnnoPratica: yearId ? Number.parseInt(yearId, 10) : undefined,
              progressivoPratica: progressive ? Number.parseInt(progressive, 10) : undefined,
              criteriRicerca: {
                codiceFiscale: cell.fiscalCode || undefined,
                idSchedaSoggettoANPR: cell.anpr || undefined,
              },
              datiRichiesta: {
                ricercaNazionale: '1',
              },
            },
          }) as ResponsePromise<HttpResponse<ResponseAnprData>, RegisterOfficeVM>
        ).then((response) => {
          const castedResponse = response as RegisterOfficeVM;

          //call the call back function which sets the subjectData

          onChangeFields({
            ...((isApplicant ? castedResponse.applicantData : castedResponse.accountholderData) ||
              {}),
            table: undefined, // reset table
            withSearch: true, // la persona è il risultato della ricerca
            // solo per intestatario
            // accordionSubjectAttributes: castedResponse.accordionSubjectAttributes || undefined,
            // citizenship: castedResponse.citizenship || undefined,
            // parenthood: castedResponse.parenthood || undefined,
            // birthdayDocument: castedResponse.birthdayDocument || undefined,
          });

          if (onChoicePerson) {
            onChoicePerson(castedResponse);
          }

          if (isApplicant) {
            // se è il richiedete non fare i controlli che seguono
            return;
          }

          if (castedResponse.accountholderData?.openModalNewPerson && setOpenModalNewPerson) {
            // open modal registrazione nuova persona
            setOpenModalNewPerson(true);
            return;
          }

          if (
            castedResponse.accountholderData?.residenceOrigin?.irreperibile &&
            setOpenModalNewPerson
          ) {
            // open modal registrazione nuova persona anche se è irreperibile
            setOpenModalNewPerson(
              true,
              castedResponse.accountholderData?.idSchedaSoggetto,
              castedResponse.accountholderData?.generality?.anprIdentifier
            );
          } else {
            if (castedResponse.accountholderData?.isSipo && setOpenModalNewPerson) {
              // open modal registrazione nuova persona
              setOpenModalNewPerson(true, castedResponse.accountholderData?.idSchedaSoggetto);
            }
          }
        });
      });
    }
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        {isSearchPersonFilterForAccountHolder ? (
          <AccordionFilterAccountHolder
            disabled={disabled}
            onChangeFields={onChangeFields}
            onChange={onChange}
            personFilterAccountholderBlur={personFilterAccountholderBlur}
            values={values}
            onSearch={onSearch}
            errors={errors}
            touched={touched}
          />
        ) : (
          <AccordionFilter
            disabled={disabled}
            onChangeFields={onChangeFields}
            onChange={onChange}
            values={values}
            onSearch={onSearch}
            errors={errors}
          />
        )}
      </Grid>
      {values?.table?.body ? (
        <Grid item xs={12}>
          <Table
            ariaLabel="Lista Persone"
            page={values?.table?.page || 0}
            rowsPerPage={values?.table?.rowsPerPage}
            body={(values?.table?.body || []) as Array<BodyTablePersonsVM>}
            count={values?.table?.body?.length || 0}
            head={headCells}
            onSort={noop}
            onPageChange={onChangePage}
            onRowsPerPageChange={onChangeRowsPerPage}
            orders={{}}
            isNotLayoutFixed
            noSort
            onVisibilityPress={onVisibilityPress}
            //hideButtons
          />
        </Grid>
      ) : undefined}
      <Grid item xs={12}>
        <FormGenerality
          disabled={disabled || disabledGenerality}
          onChangeGenerality={onFieldGeneralityChange}
          onChangeFieldsGenerality={onFieldsGeneralityChange}
          generality={values?.generality}
          errorsGenerality={(errors?.generality || {}) as FormikErrors<AccordionGeneralityForm>}
          touchedGenerality={(touched?.generality || {}) as FormikTouched<AccordionGeneralityForm>}
          labelMunicipalityItalian="Comune di nascita"
          labelProvinceItalian="Provincia di nascita"
        />
      </Grid>
    </Grid>
  );
};

GeneralityComponent.displayName = 'Generality component';

const Generality = memo(GeneralityComponent);

export default Generality;
